import { Box, Button, TextField, Typography } from '@mui/material';
import { FormProvider, Form, Controller } from 'react-hook-form';
import { useSummaryNotes } from './hooks';
import { paths } from '@common/types';
import { isNil } from 'lodash';
import { t } from 'i18next';
import { theme } from '@store';
import { SaveChangesModal } from '@components';
import { MAXIMUM_TEXT_AREA_LENGTH } from '@common/types/constants.ts';

interface ISummaryNotesProps {
  patient: paths[`/patient/{patient_id}`]['get']['responses']['200']['content']['application/json'];
  disabled: boolean;
}

export const SummaryNotes = ({ patient, disabled }: ISummaryNotesProps) => {
  const {
    formMethods,
    onSubmit,
    isOpen,
    handleOpen,
    handleClose,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    isValid,
    isEditSubmitting,
  } = useSummaryNotes(patient);

  const {
    control,
    formState: { isSubmitting },
  } = formMethods;

  return (
    <Box
      sx={{
        width: '100%',
      }}
    >
      {isOpen ? (
        <FormProvider {...formMethods}>
          <Form
            control={control}
            onSubmit={e => onSubmit(e.event)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              height: 'fit-content',
              position: 'relative',
              gap: '1rem',
              marginTop: 8,
            }}
          >
            <Controller
              name={'notes'}
              control={control}
              disabled={disabled}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  sx={{ bgcolor: 'white' }}
                  type='text'
                  size='medium'
                  label='Add notes'
                  variant='outlined'
                  error={!isNil(error)}
                  helperText={
                    error
                      ? error.message
                      : `${field.value ? field.value.length + `/${MAXIMUM_TEXT_AREA_LENGTH}` : ''}`
                  }
                  fullWidth
                  multiline
                  minRows={4}
                  maxRows={4}
                  autoFocus
                  slotProps={{
                    formHelperText: {
                      style: {
                        backgroundColor: '#F5F5F5',
                        textAlign: error ? 'left' : 'right',
                        margin: 0,
                        padding: '3px 14px 0',
                      },
                    },
                  }}
                />
              )}
            />

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '8px',
                position: 'sticky',
                bottom: 0,
                paddingBottom: 3,
                zIndex: 3,
              }}
            >
              <Button
                variant='text'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                }}
                disabled={isSubmitting || disabled}
                onClick={handleClose}
              >
                {t('general.cancel')}
              </Button>
              <Button
                type='submit'
                variant='contained'
                color='secondary'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                  color: 'white',
                }}
                disabled={isSubmitting || disabled || !isValid}
              >
                {t('general.save')}
              </Button>
            </Box>
            <SaveChangesModal
              isOpen={isSaveChangesModalOpen}
              onClose={handleCloseSaveChangesModal}
              isValid={isValid}
              handleSaveChanges={handleSaveChanges}
              discardChanges={handleDiscardChanges}
              isLoading={isEditSubmitting}
            />
          </Form>
        </FormProvider>
      ) : patient.notes ? (
        <Box
          sx={{
            p: '0.5rem 1rem',
            bgcolor: theme.palette.blue[50],
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            border: `1px solid ${theme.palette.blue[100]}`,
          }}
        >
          <Typography
            sx={{
              fontStyle: 'italic',
              fontSize: '0.875rem',
              wordBreak: 'break-all',
              whiteSpace: 'pre-wrap',
              maxHeight: '200px',
              overflow: 'auto',
              paddingRight: '0.5rem',
              scrollbarGutter: 'stable',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#666',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                backgroundColor: '#999',
              },
              width: '100%',
            }}
          >
            {patient.notes}
          </Typography>
          <Button
            onClick={handleOpen}
            variant='text'
            disabled={disabled}
            sx={{
              fontSize: '0.8125rem',
              color: theme.palette.primary.main,
              alignSelf: 'self-start',
            }}
          >
            EDIT
          </Button>
        </Box>
      ) : (
        <TextField
          placeholder='Add notes'
          onClick={handleOpen}
          disabled={disabled}
          slotProps={{
            input: {
              readOnly: true,
            },
          }}
          sx={{
            width: '100%',
            bgcolor: 'white',
            cursor: 'pointer',
            '& .MuiInputBase-root': {
              cursor: 'pointer',
            },
            '&:hover .MuiInputBase-root': {
              bgcolor: theme.palette.grey[50],
              borderRadius: '6px',
            },
          }}
        />
      )}
    </Box>
  );
};
